import { INSURANCE_TYPES_NAMES } from 'constants/insurance-types-names';
import {
  ADMIN_ROUTE,
  EXPLORE_ROUTE,
  ADVICE_ROUTE,
  HOME_ROUTE,
  DASHBOARD_ROUTE,
  MARKET_SCAN_NEW_DEFAULT_ROUTE,
  MARKET_SCAN_ROUTE,
  OPERATIONS_ROUTE,
  EXPLORE_COVERAGE_ROUTE,
  ADD_POLICY_ROUTE,
  EDIT_POLICY_ROUTE,
  PROFILE_ROUTE,
  WINDCAVE_CALLBACK_BASE_ROUTE,
  NOTIFICATIONS_ROUTE,
  MARKET_SCAN_PURCHASE_ROUTE,
  WORKPLACE_ROUTE,
  LOGOUT_ROUTE,
  DISCLOSURE_ROUTE,
  WINDCAVE_CALLBACK_PROTECTA,
  WINDCAVE_CALLBACK_STAR,
} from 'constants/routes';
import { renameIfProtecta } from 'helpers';
import { IPolicyScan, POLICY_SCAN_STATUS } from 'types';
import carIcon from 'assets/icons/car.svg';
import motorbikeIcon from 'assets/icons/motorbike.svg';
import homeAndContentsIcon from 'assets/icons/home-and-contents.svg';
import rvIcon from 'assets/icons/rv.svg';
import inReviewIcon from 'assets/icons/inReview.svg';
import inProgressIcon from 'assets/icons/inProgress.svg';
import lifeIcon from 'assets/icons/life.svg';

const exactMap = {
  [HOME_ROUTE]: 'Home',
  [DASHBOARD_ROUTE]: 'My Dashboard',
  [DISCLOSURE_ROUTE]: 'Disclosure & declaration',
  [MARKET_SCAN_NEW_DEFAULT_ROUTE]: 'Market Scan',
  [EXPLORE_COVERAGE_ROUTE]: 'Insurance checkup',
  [ADD_POLICY_ROUTE]: 'Add policy',
  [EDIT_POLICY_ROUTE]: 'Edit policy',
  [NOTIFICATIONS_ROUTE]: 'Notifications',
  [LOGOUT_ROUTE]: 'Log out',
};

const includesMap = [
  {
    route: ADVICE_ROUTE,
    name: 'Compare with an adviser',
  },
  {
    route: EXPLORE_ROUTE,
    name: 'Explore Insurance',
  },
  {
    route: WINDCAVE_CALLBACK_PROTECTA,
    name: 'Assurant Purchase',
  },
  {
    route: WINDCAVE_CALLBACK_STAR,
    name: 'Star Insure Purchase',
  },
  {
    route: MARKET_SCAN_PURCHASE_ROUTE,
    name: 'Purchase',
  },
  {
    route: MARKET_SCAN_ROUTE,
    name: 'Market Scan',
  },
  {
    route: ADMIN_ROUTE,
    name: 'Admin',
  },
  {
    route: OPERATIONS_ROUTE,
    name: 'Operations',
  },
  {
    route: '/policy/',
    name: 'Policy details',
  },
  {
    route: WORKPLACE_ROUTE,
    name: 'My Workplace',
  },
  {
    route: PROFILE_ROUTE,
    name: 'My Profile',
  },
];

export const getPageName = (location) => {
  const pathName = location.pathname;
  const exact = exactMap[pathName];
  if (exact) {
    return exact;
  }

  const included = includesMap.find((m) => pathName.includes(m.route));
  if (included && included?.name === 'Purchase') {
    const urlParams = new URLSearchParams(location.search);
    let providerName = urlParams.get('providerName') || '';
    providerName = renameIfProtecta(providerName);
    return `${providerName} ${included.name}`;
  }
  return included?.name;
};

const getVehicleNavbarTitle = (policyScan: IPolicyScan, status: string) => {
  if (!policyScan) {
    return ""
  }
  const { numberPlate, model } = policyScan;
  if (numberPlate) {
    return `Market Scan for ${numberPlate.toUpperCase()} ${status}`;
  } else if (model) {
    return `Market Scan for ${model} ${status}`;
  } else {
    return `Market Scan for your vehicle ${status}`;
  }
}

export const getNavbarTitle = (
  policyScan?: IPolicyScan,
  navbarTitle?: string,
) => {
  if (!policyScan) {
    return 'Market Scan';
  }

  if (policyScan.status === POLICY_SCAN_STATUS.IN_REVIEW) {
    return 'Results in review';
  }

  const status =
    policyScan?.status !== POLICY_SCAN_STATUS.READY &&
      policyScan?.status !== POLICY_SCAN_STATUS.VIEWED
      ? 'is in progress'
      : '';

  switch (policyScan.policyType) {
    case INSURANCE_TYPES_NAMES.MOTORCYCLE:
      return `Market Scan for ${policyScan.numberPlate?.toUpperCase() || 'your motorcycle'} ${status}`;
    case INSURANCE_TYPES_NAMES.VEHICLE:
      return getVehicleNavbarTitle(policyScan, status);
    case INSURANCE_TYPES_NAMES.RV:
      return `Market Scan for ${policyScan.numberPlate?.toUpperCase() || 'your vehicle'} ${status}`;
    case INSURANCE_TYPES_NAMES.MBI:
      return `Market Scan for ${policyScan.numberPlate?.toUpperCase() || 'your vehicle'} ${status}`;

    case INSURANCE_TYPES_NAMES.LIFE:
      return (
        navbarTitle || `Life Market Scan for ${policyScan.userName} ${status}`
      );
    default:
      return `Market Scan for ${policyScan.address} ${status}`;
  }
};

export const getNavbarIcon = (
  policyScan?: IPolicyScan,
  navbarTitle?: string,
) => {
  if (navbarTitle && navbarTitle.includes('in progress')) {
    return inProgressIcon;
  }

  if (!policyScan) {
    return inProgressIcon;
  }

  if (policyScan.status === POLICY_SCAN_STATUS.IN_REVIEW) {
    return inReviewIcon;
  }

  switch (policyScan.policyType) {
    case INSURANCE_TYPES_NAMES.VEHICLE:
      return carIcon;
    case INSURANCE_TYPES_NAMES.MOTORCYCLE:
      return motorbikeIcon;
    case INSURANCE_TYPES_NAMES.RV:
      return rvIcon;
    case INSURANCE_TYPES_NAMES.MBI:
      // null signals NavBar to render the MBIIconV2 svg instead of an <img>.
      return null;
    case INSURANCE_TYPES_NAMES.LIFE:
      return lifeIcon;
    default:
      return homeAndContentsIcon;
  }
};
